import { useEffect, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/modules/auth/AuthContext";

import { Button } from "@/design-system/primitives/Button";
import { LandingAurora } from "./LandingAurora";
import { Wordmark } from "@/design-system/primitives/Wordmark";
import { PricingTable } from "@/modules/billing/PricingTable";
import {
  Waypoints,
  Handshake,
  Mountain,
  Target,
  LineChart,
  CalendarCheck,
  ListChecks,
  Network,
  Users,
  ImageIcon,
} from "lucide-react";

/* ── Shared DS style fragments ─────────────────────────────────────────────
   Layout stays on Tailwind utilities; every color/type/surface decision reads
   --ds-* tokens so the page renders correctly in both Daylight and Aurora. */

const display: CSSProperties = {
  fontFamily: "var(--ds-font-display)",
  color: "hsl(var(--ds-text-heading))",
  letterSpacing: "-0.02em",
  textWrap: "balance",
};

// Hover lift for interactive-feeling glass cards (pillars, feature items).
const cardLift = {
  onMouseEnter: (e: MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = "var(--ds-shadow-md)";
  },
  onMouseLeave: (e: MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = "var(--ds-shadow-sm)";
  },
};

const bodyText: CSSProperties = {
  color: "hsl(var(--ds-text-body-color))",
  lineHeight: "var(--ds-leading-body)",
};

const metaText: CSSProperties = { color: "hsl(var(--ds-text-meta))" };

const eyebrowStyle: CSSProperties = {
  fontSize: "var(--ds-text-eyebrow)",
  letterSpacing: "var(--ds-tracking-eyebrow)",
  textTransform: "uppercase",
  fontWeight: 700,
  color: "hsl(var(--ds-brand-purple-fg))",
};

const glassCard: CSSProperties = {
  background: "var(--ds-glass-card-fill)",
  border: "1px solid var(--ds-glass-card-border)",
  backdropFilter: "var(--ds-glass-blur)",
  WebkitBackdropFilter: "var(--ds-glass-blur)",
  borderRadius: "var(--ds-radius-lg)",
  boxShadow: "var(--ds-shadow-sm)",
  transition: "transform var(--ds-dur-fast) var(--ds-ease-soft), box-shadow var(--ds-dur-fast) var(--ds-ease-soft)",
};

function Eyebrow({ children }: { children: ReactNode }) {
  return <p style={eyebrowStyle}>{children}</p>;
}

function NavAnchor({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="hidden sm:block text-sm font-semibold px-3 py-2 transition-colors"
      style={metaText}
      onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-heading))")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-meta))")}
    >
      {children}
    </a>
  );
}

// Stand-in for real product screenshots/video. One-line swap per slot once
// assets exist: replace the placeholder with an <img>/<video> at the same spot.
function AssetPlaceholder({ label, aspect = "16 / 9", className = "" }: { label: string; aspect?: string; className?: string }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 text-center px-6 ${className}`}
      style={{
        ...glassCard,
        aspectRatio: aspect,
        border: "1px dashed var(--ds-glass-card-border)",
      }}
    >
      <ImageIcon className="w-6 h-6" style={{ color: "hsl(var(--ds-text-meta))" }} strokeWidth={1.5} />
      <p className="text-xs font-semibold" style={metaText}>
        {label}
      </p>
    </div>
  );
}

const pillars = [
  {
    icon: Waypoints,
    word: "Integrate",
    tagline: "From vision to results, leader to contributor.",
    body: "The connective tissue between your long-term direction and what gets done this week, across every level of the team.",
  },
  {
    icon: Handshake,
    word: "Integrity",
    tagline: "Do what you said you'd do.",
    body: "Structure works, if it's built right. A shared system keeps the team aligned and accountable — but only if the culture backs it up.",
  },
  {
    icon: Mountain,
    word: "Grit",
    tagline: "Hard things still take human judgment.",
    body: "No software decides what matters, who owns it, or whether it's truly done. The hard calls are still yours.",
  },
];

// Condensed from three grouped sections into one grid — supporting detail,
// not the argument (the week walkthrough carries that now).
const platformFeatures = [
  {
    icon: Target,
    title: "Vision & Goals",
    body: "Long-term direction, annual goals, and quarterly rocks — connected from boardroom to backlog.",
  },
  {
    icon: Network,
    title: "Accountability Chart",
    body: "Roles and seats, not just a reporting structure. Make ownership — and the reasoning behind it — unambiguous.",
  },
  {
    icon: LineChart,
    title: "Scorecard & Metrics",
    body: "Weekly numbers with thresholds and trend lines that flag problems before they compound. Know what's off before the month closes.",
  },
  {
    icon: Users,
    title: "Team Health",
    body: "See where momentum is building and where it's quietly stalling. Patterns across people and quarters surface the friction that one-on-ones miss.",
  },
  {
    icon: CalendarCheck,
    title: "Weekly Meetings",
    body: "A guided cadence that ends with clear owners, dates, and decisions — not another meeting.",
  },
  {
    icon: ListChecks,
    title: "Issues & To-Dos",
    body: "Captured when they surface, assigned before the meeting ends, resolved before the next one. Nothing carries forward by accident.",
  },
];

// "A week in Integritty" — the true time sequence that anchors the page (see
// section 3 below). Numbering is legitimate because it's a real weekly order.
const weekSteps = [
  {
    step: "01",
    title: "Monday, the numbers come in.",
    body: "Scorecard entries land before the meeting starts, so the team walks in already knowing what's off.",
    asset: "UI crop — Scorecard entry",
  },
  {
    step: "02",
    title: "The meeting runs itself.",
    body: "A guided agenda moves the room through the scorecard, the quarter's rocks — your 90-day objectives — and open issues, without anyone chasing the clock.",
    asset: "UI crop — Weekly Meeting view",
  },
  {
    step: "03",
    title: "Owners and dates leave the room assigned.",
    body: "Every issue that comes up gets a name and a date before the meeting ends — nothing carries forward by accident.",
    asset: "UI crop — Issues & To-Dos",
  },
  {
    step: "04",
    title: "Rocks tick forward on the quarter.",
    body: "Progress updates roll up automatically, so the quarterly view is always a true read of where things stand.",
    asset: "UI crop — Rocks board",
  },
];

// Ordered for the mixed avatar: first-timer objections first (overkill fear,
// no-methodology fear), then the questions an EOS-aware shopper asks.
const faqs = [
  {
    q: "We're a small team — isn't this overkill?",
    a: (
      <>
        No — small is when the rhythm is cheapest to build. New habits take time either way, and the fewer people at
        the table, the faster they set. The team you hire next year will adopt whatever cadence already exists. The
        best time to start is <em>before</em> your systems (or lack thereof) fail.
      </>
    ),
  },
  {
    q: "I've never run EOS or read Traction. Do I need to start there?",
    a: "Not at all. Getting started with a business operating system can be overwhelming, but Integritty shows you the way at the right pace, while offering full features for seasoned operators to jump in right away.",
  },
  {
    q: "Will my team actually use it?",
    a: "You still have to set the tone — no software fixes a culture problem. But Integritty surfaces just what each person needs, just when they need it: the number to enter, the to-do to check off, the issue to raise when something's stuck. Thirty seconds at a time, not another system to live in. Buy-in is a feature, and we build for it.",
  },
  {
    q: "How is this different from Ninety, EOS One, or a spreadsheet?",
    a: "Tools built for one methodology make you speak their language; Integritty adapts to the way your team already works — rename Rocks, Issues, any of it, and keep the discipline underneath. And a spreadsheet can hold your numbers, but it can't run your meeting: no agenda, no owners, no follow-through. Integritty is the middle path — more freedom than the EOS-branded tools, more guidance than DIY.",
  },
  {
    q: "How long does setup take?",
    a: "About 5 minutes to your first meeting. Sign up, and a short tutorial walks you through the few steps that matter — you don't need to be a power user on day one, and you don't need everything configured before the value starts.",
  },
];

export default function LandingRoute() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("landing-smooth-scroll");
    return () => document.documentElement.classList.remove("landing-smooth-scroll");
  }, []);

  const goToDashboard = () => navigate("/dashboard");
  const goToOnboarding = () => navigate(user ? "/onboarding" : "/login?redirect=/onboarding");
  const primaryAction = user ? goToDashboard : goToOnboarding;
  const primaryLabel = user ? "Go to dashboard" : "Start for free";

  return (
    <div className="min-h-screen relative" style={{ fontFamily: "var(--ds-font-body)", ...bodyText }}>
      {/* One continuous aurora field for the whole page — blobs spread down the
          full scroll height with per-blob parallax (see LandingAurora). Every
          glass surface has exactly one blur between its text and this field. */}
      <LandingAurora />

      <div className="relative" style={{ zIndex: 2 }}>
        {/* Nav — scrimless floating chrome, so it carries its own light glass */}
        <header
          className="sticky top-0 z-40"
          style={{
            background: "var(--ds-glass-popover-fill)",
            backdropFilter: "var(--ds-glass-popover-blur)",
            WebkitBackdropFilter: "var(--ds-glass-popover-blur)",
            borderBottom: "1px solid var(--ds-topbar-border)",
          }}
        >
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <Wordmark />
            <nav className="flex items-center gap-2">
              <NavAnchor href="#week">How it works</NavAnchor>
              <NavAnchor href="#platform">Platform</NavAnchor>
              <NavAnchor href="#pricing">Pricing</NavAnchor>
              <NavAnchor href="#why">Why Integritty</NavAnchor>
              {user ? (
                <Button onClick={goToDashboard} icon="arrow-right" iconRight>
                  Go to dashboard
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/login")}>
                    Sign in
                  </Button>
                  <Button onClick={goToOnboarding} icon="arrow-right" iconRight>
                    Start for free
                  </Button>
                </>
              )}
            </nav>
          </div>
        </header>

        <main>
          {/* Hero — names the category up front, real product visual carries the rest */}
          <section>
            <div className="max-w-6xl mx-auto px-6 pt-14 pb-10 md:pt-16 md:pb-12 text-center">
              <div
                className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold"
                style={{
                  ...glassCard,
                  borderRadius: "var(--ds-radius-pill)",
                  color: "hsl(var(--ds-text-meta))",
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: "hsl(var(--ds-brand-purple-fg))" }}
                />
                Built for operators
              </div>
              <h1
                className="mt-6 leading-[1.04]"
                style={{ ...display, fontWeight: 800, fontSize: "clamp(40px, 6.5vw, 68px)" }}
              >
                Your business should
                <br />
                run without you.
              </h1>
              <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl" style={bodyText}>
                Integritty is the weekly operating system for small teams — goals, scorecard, meetings, and to-dos in
                one rhythm.
              </p>
              <div className="mt-10 flex flex-col items-center gap-3">
                <Button size="lg" onClick={primaryAction} icon="arrow-right" iconRight>
                  {primaryLabel}
                </Button>
                {!user && (
                  <p className="text-xs" style={metaText}>
                    Run your first meeting this week
                  </p>
                )}
              </div>
            </div>

            {/* Product visual */}
            <div className="max-w-6xl mx-auto px-6 pb-16">
              <AssetPlaceholder label="App screenshot — Scorecard or Weekly Meeting view" aspect="16 / 9" />
            </div>
          </section>

          {/* Outcomes — promoted from supporting detail to the page's second beat */}
          <section>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl">
                <Eyebrow>The turning point</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  What got you here won't get you there.
                </h2>
                <p className="mt-4 text-lg" style={bodyText}>
                  Hustle, memory, and being in every room built the business. Maybe the team has outgrown them — or
                  maybe you're running your own company for the first time, after years of executing someone else's
                  plan. Either way, the next stage doesn't run on effort. It runs on a system.
                </p>
              </div>
              <div className="mt-14 space-y-4">
                {[
                  "Your team stops asking you what to do.",
                  "You stop finding out about problems a month too late.",
                  "Meetings stop holding you back and start moving the needle.",
                ].map((statement, i) => (
                  <p
                    key={statement}
                    style={{
                      paddingLeft: `${i * 2}rem`,
                      ...display,
                      fontWeight: 700,
                      color: "hsl(var(--ds-text-strong) / 0.85)",
                    }}
                    className="text-xl md:text-2xl leading-snug"
                  >
                    {statement}
                  </p>
                ))}
              </div>
            </div>
          </section>

          {/* A week in Integritty — the page's spine, a true time sequence */}
          <section id="week" style={{ scrollMarginTop: 72 }}>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl">
                <Eyebrow>A week in Integritty</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  From Monday numbers to Friday progress.
                </h2>
              </div>
              <div className="mt-16 space-y-16">
                {weekSteps.map(({ step, title, body, asset }, i) => (
                  <div key={step} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
                    <div className={i % 2 === 1 ? "md:order-2" : undefined}>
                      <span
                        className="text-sm font-bold tabular-nums"
                        style={{ color: "hsl(var(--ds-brand-purple-fg))" }}
                      >
                        {step}
                      </span>
                      <h3 className="mt-2 text-2xl md:text-3xl" style={{ ...display, fontWeight: 800 }}>
                        {title}
                      </h3>
                      <p className="mt-3 text-lg" style={bodyText}>
                        {body}
                      </p>
                    </div>
                    <AssetPlaceholder label={asset} aspect="4 / 3" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Watch it run — low-commitment mid-funnel path */}
          <section>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl mx-auto text-center">
                <Eyebrow>See it for yourself</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  Watch it run.
                </h2>
                <p className="mt-4 text-lg" style={bodyText}>
                  A real weekly meeting, start to finish.
                </p>
              </div>
              <div className="mt-12 max-w-3xl mx-auto">
                <AssetPlaceholder label="60–90s product video / interactive demo" aspect="16 / 9" />
              </div>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" onClick={primaryAction} icon="arrow-right" iconRight>
                  {primaryLabel}
                </Button>
                <Button size="lg" variant="ghost" disabled>
                  Watch a meeting run
                </Button>
              </div>
            </div>
          </section>

          {/* Platform — condensed grid, supporting detail not the argument */}
          <section id="platform" style={{ scrollMarginTop: 72 }}>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl">
                <Eyebrow>The platform</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  Every layer, connected.
                </h2>
                <p className="mt-4 text-lg" style={bodyText}>
                  If you already run EOS or another Traction-style system, everything here will feel familiar on day
                  one. If you've never heard of it, you don't need to — the rhythm teaches itself.
                </p>
              </div>
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {platformFeatures.map(({ icon: Icon, title, body }) => (
                  <div key={title} className="p-7" style={glassCard} {...cardLift}>
                    <div
                      className="w-10 h-10 flex items-center justify-center"
                      style={{
                        borderRadius: "var(--ds-radius-sm)",
                        background: "hsl(var(--ds-brand-purple-bg))",
                        color: "hsl(var(--ds-brand-purple-fg))",
                      }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <h4 className="mt-5 text-lg" style={{ ...display, fontWeight: 700 }}>
                      {title}
                    </h4>
                    <p className="mt-2 text-sm" style={bodyText}>
                      {body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Proof — ships real or ships cut; no fabricated quotes */}
          <section>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl mx-auto text-center">
                <Eyebrow>What operators say</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  What changes when your team has a real system.
                </h2>
              </div>
              <div className="mt-12 max-w-4xl mx-auto">
                <AssetPlaceholder label="Customer quotes / logos / usage stat" aspect="21 / 9" />
              </div>
            </div>
          </section>

          {/* Why "Integritty" — founder close */}
          <section id="why" style={{ scrollMarginTop: 72 }}>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl">
                <Eyebrow>Why Integritty</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  Integrate with integrity and grit.
                </h2>
                <p className="mt-4 text-lg" style={bodyText}>
                  I spent years as the operator in the room — trying to get business owners out of the weeds and
                  growing teams pointed in the same direction. Every tool we tried was either too rigid, too shallow,
                  or only useful to whoever set it up. I built Integritty to close that gap.
                </p>
                <p className="mt-3 text-sm" style={metaText}>
                  — Josh, Founder of Integritty
                </p>
              </div>
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                {pillars.map(({ icon: Icon, word, tagline, body }) => (
                  <div key={word} className="p-8" style={glassCard} {...cardLift}>
                    <div
                      className="w-11 h-11 flex items-center justify-center"
                      style={{
                        borderRadius: "var(--ds-radius-sm)",
                        background: "hsl(var(--ds-brand-purple-bg))",
                        color: "hsl(var(--ds-brand-purple-fg))",
                      }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 text-2xl" style={{ ...display, fontWeight: 800 }}>
                      {word}
                    </h3>
                    <p className="mt-2 font-semibold" style={{ color: "hsl(var(--ds-text-strong))" }}>
                      {tagline}
                    </p>
                    <p className="mt-3" style={bodyText}>
                      {body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section id="pricing" style={{ scrollMarginTop: 72 }}>
            {/* Wider than the other sections: four priced rungs sit side by side here. */}
            <div className="max-w-7xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl mx-auto text-center">
                <Eyebrow>Pricing</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  Simple, transparent pricing.
                </h2>
                <p className="mt-4 text-lg" style={bodyText}>
                  Start free. Upgrade when your team is ready.
                </p>
              </div>
              <div className="mt-12">
                <PricingTable
                  onSelectPlan={(tier, cadence) => {
                    const dest = `/onboarding?tier=${tier}&cadence=${cadence}`;
                    if (user) {
                      navigate(dest);
                    } else {
                      navigate(`/login?redirect=${encodeURIComponent(dest)}`);
                    }
                  }}
                />
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <div className="max-w-6xl mx-auto px-6 py-24" style={{ borderTop: "1px solid var(--ds-divider)" }}>
              <div className="max-w-2xl mx-auto text-center">
                <Eyebrow>Questions</Eyebrow>
                <h2 className="mt-3 text-3xl md:text-5xl" style={{ ...display, fontWeight: 800 }}>
                  Before you dive in.
                </h2>
              </div>
              <div className="mt-12 max-w-2xl mx-auto space-y-8">
                {faqs.map(({ q, a }) => (
                  <div key={q}>
                    <h3 className="text-lg font-semibold" style={{ color: "hsl(var(--ds-text-strong))" }}>
                      {q}
                    </h3>
                    <p className="mt-2" style={bodyText}>
                      {a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Closing CTA — the one modal-weight surface on the page */}
          <section>
            <div className="max-w-6xl mx-auto px-6 py-24">
              <div
                className="p-12 md:p-16 text-center"
                style={{
                  background: "var(--ds-glass-modal-fill)",
                  border: "1px solid var(--ds-glass-card-border)",
                  backdropFilter: "var(--ds-glass-blur)",
                  WebkitBackdropFilter: "var(--ds-glass-blur)",
                  borderRadius: "var(--ds-radius-xl)",
                  boxShadow: "var(--ds-shadow-md)",
                }}
              >
                <h2
                  className="text-3xl md:text-5xl max-w-3xl mx-auto leading-tight"
                  style={{ ...display, fontWeight: 800 }}
                >
                  Stop chasing your team. Start running the business.
                </h2>
                <p className="mt-5 text-lg max-w-xl mx-auto" style={bodyText}>
                  Get your strategy, scorecard, and meetings into one rhythm — and build a team that's actually excited
                  to show up every week.
                </p>
                <div className="mt-8 flex flex-col items-center gap-3">
                  <Button size="lg" onClick={primaryAction} icon="arrow-right" iconRight>
                    {primaryLabel}
                  </Button>
                  {!user && (
                    <p className="text-xs" style={metaText}>
                      No credit card required · Cancel anytime
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer style={{ borderTop: "1px solid var(--ds-divider)" }}>
          <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <Wordmark />
              <p className="mt-3 text-sm max-w-xs" style={metaText}>
                Business ops software your team will actually want to use.
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: "hsl(var(--ds-text-strong))" }}>
                Product
              </p>
              <ul className="mt-3 space-y-2">
                {[
                  { label: "How it works", href: "#week" },
                  { label: "Platform", href: "#platform" },
                  { label: "Pricing", href: "#pricing" },
                  { label: "Why Integritty", href: "#why" },
                ].map(({ label, href }) => (
                  <li key={label}>
                    <a
                      className="text-sm transition-colors"
                      style={metaText}
                      href={href}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-heading))")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-meta))")}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--ds-divider)" }}>
            <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
              <p className="text-xs" style={metaText}>
                © {new Date().getFullYear()} Integritty. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <Link
                  to="/privacy"
                  className="text-xs transition-colors"
                  style={metaText}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-heading))")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--ds-text-meta))")}
                >
                  Privacy Policy
                </Link>
                <p className="text-xs" style={metaText}>
                  Made for operators.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
